import { OutlinedButton } from "@/components/ui/buttons";
import { QueryKeys } from "@/constants/query-keys";
import { WeightInputDialog, WeightInputDialogHandle } from "@/features/user-profile/components/weight-input-dialog";
import { useGetBodyStats } from "@/hooks/requests/body-status/use-get-body-stats";
import { useUpdateBodyStats } from "@/hooks/requests/body-status/use-update-body-stats";
import { useToast } from "@/hooks/ui/use-toast";
import { cn } from "@/utils/cn";
import { textVariants } from "@/utils/text-variants";
import { useQueryClient } from "@tanstack/react-query";
import { memo, useRef, useState } from "react";

const formatWeight = (value?: string, unit?: string) => {
  const parsedValue = Number(value ?? 0);

  if (!Number.isFinite(parsedValue) || parsedValue <= 0) {
    return "--";
  }

  const formatted = new Intl.NumberFormat("en-GB", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(parsedValue);

  return `${formatted} ${unit ?? "kg"}`;
};

export const OverviewWeightCard = memo(function OverviewWeightCard() {
  const [isWeightModalOpen, setIsWeightModalOpen] = useState(false);
  const dialogRef = useRef<WeightInputDialogHandle>(null);
  const { bodyStats } = useGetBodyStats();
  const { updateBodyStatsAsync } = useUpdateBodyStats();
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();

  const openDialog = () => {
    dialogRef.current?.focusInput();
    setIsWeightModalOpen(true);
  };

  const weight = bodyStats?.currentWeight ? Number(bodyStats.currentWeight) : 0;
  const weightUnit = bodyStats?.currentWeightUnit || "kg";

  const handleWeightUpdate = async (value: string, unit: "kg" | "lbs") => {
    try {
      await updateBodyStatsAsync({
        height: bodyStats?.height || "170",
        heightUnit: bodyStats?.heightUnit || "cm",
        currentWeight: value,
        currentWeightUnit: unit,
        targetWeight: bodyStats?.targetWeight || value,
        targetWeightUnit: bodyStats?.targetWeightUnit || unit,
        startingWeight: bodyStats?.startingWeight || value,
        startingWeightUnit: bodyStats?.startingWeightUnit || unit,
      });
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.BODY_STATS] });
      showSuccess("Weight updated successfully");
    } catch (error) {
      showError("Failed to update weight");
      console.error(error);
    }
  };

  return (
    <div
      className={cn(
        "flex flex-col gap-4 rounded-4xl bg-black/10 px-4.5 py-6",
        "border border-[rgba(255,255,255,0.06)]",
      )}
    >
      <div className="flex justify-between gap-3">
        <div className="flex flex-1 flex-col items-center gap-[19px] rounded-[19px] border border-white/8 px-4 py-3">
          <p className={cn(textVariants.footnote, "text-white")}>Started weight</p>
          <p className={cn(textVariants.title2, "text-white")}>
            {formatWeight(bodyStats?.startingWeight, bodyStats?.startingWeightUnit || "kg")}
          </p>
        </div>

        <button
          type="button"
          onClick={openDialog}
          className="flex flex-1 flex-col items-center gap-[19px] rounded-[19px] border border-white/8 px-4 py-3"
        >
          <p className={cn(textVariants.footnote, "text-white")}>Current weight</p>
          <p className={cn(textVariants.title2, "text-white")}>
            {formatWeight(bodyStats?.currentWeight, bodyStats?.currentWeightUnit || "kg")}
          </p>
        </button>
      </div>

      <div className="flex justify-center">
        <OutlinedButton
          color="white"
          textCase="normal"
          className="h-11.5 w-39 border-white"
          onClick={openDialog}
        >
          <span className={cn(textVariants.bodySmall, "text-white")}>Update Weight</span>
        </OutlinedButton>
      </div>

      <WeightInputDialog
        ref={dialogRef}
        isOpen={isWeightModalOpen}
        onDismiss={() => setIsWeightModalOpen(false)}
        defaultValue={weight.toString()}
        defaultUnit={weightUnit}
        onSelect={handleWeightUpdate}
      />
    </div>
  );
});
